/**
 * Calaméo guarantee sheets ("tableau des garanties" PDF) per formula slug.
 *
 * These are the documents linked from every "En savoir +" panel: the sales
 * journey (connected and non-connected alike, both served by
 * SubscriptionStartCard) and the connected space's contract detail.
 *
 * Why a standalone table rather than a `sheetUrl` field inside the guarantee
 * content of `guaranteeDetails.ts`:
 *  - the URL is locale-independent, while the content there is keyed by locale;
 *    embedding it would mean repeating the same URL in the `fr` and `en` blocks
 *    and letting them drift apart;
 *  - several formulas of the same commercial product share one sheet (the two
 *    AVE offers, the two AURA formulas), so the mapping is many-to-one and reads
 *    far better grouped by document than scattered across formula entries;
 *  - a formula can have guarantee rows but no published sheet (and vice versa),
 *    so the two concerns have genuinely independent lifecycles.
 *
 * Keys are the front-end formula slugs — the same ones used by `planReference`
 * in `src/subscriptionFlows/*` (the `tierKey` of a results card) and by
 * `BUNDLE_REFERENCE_TO_PLAN` in `bundleReference.ts` (the connected space).
 * That shared vocabulary is what lets one table serve all three surfaces.
 *
 * A formula with no entry here intentionally renders no link rather than
 * falling back to a neighbouring document: showing the wrong guarantee table is
 * worse than showing none. Adding a product is a one-line change.
 */

/** Guarantee sheet shared by the two AVE offers (Indispensable / Sécurité). */
const AVE_SHEET = 'https://www.calameo.com/read/00550037515e35457b389'

/** Guarantee sheet of the « Pack en France » Confort pack. */
const PEF_CONFORT_SHEET = 'https://www.calameo.com/read/00550037505d9d6fccc95'

/** Guarantee sheet of the « Pack en France » Global pack. */
const PEF_GLOBAL_SHEET = 'https://www.calameo.com/read/005500375de81cf2c5acb'

/** Guarantee sheet of the Tonic-Études top-up. */
const TONIC_SHEET = 'https://www.calameo.com/read/005500375a476de9f90a8'

/** Guarantee sheet of the Auvergne-Rhône-Alpes regional plans. */
const AURA_SHEET = 'https://www.calameo.com/read/005500375deb2daae51d7'

export const GUARANTEE_SHEET_URL_BY_SLUG: Readonly<Partial<Record<string, string>>> = {
  // AVE (responsabilité civile étudiante) — both offers share one sheet.
  indispensable: AVE_SHEET,
  security: AVE_SHEET,
  // PEF — « Pack en France » Confort and Global, one sheet each.
  packFranceConfort: PEF_CONFORT_SHEET,
  packFranceGlobal: PEF_GLOBAL_SHEET,
  // Surcomplémentaire Tonic-Études.
  tonic: TONIC_SHEET,
  // Mutuelle AURA — both formulas share one sheet.
  primordiale: AURA_SHEET,
  renforcee: AURA_SHEET,
}

/**
 * The guarantee sheet URL for a formula slug, or `null` when the formula has no
 * published sheet. Nullish slugs are accepted so callers can pass an unresolved
 * lookup (e.g. an unmapped bundle reference) without guarding first.
 */
export function guaranteeSheetUrlFor(slug: string | null | undefined): string | null {
  if (!slug) return null
  // `Object.hasOwn` rather than a plain index read: slugs originate from backend
  // bundle data, and a value such as "constructor" or "toString" would otherwise
  // resolve to an inherited Object.prototype member and be rendered as an href.
  if (!Object.hasOwn(GUARANTEE_SHEET_URL_BY_SLUG, slug)) return null
  return GUARANTEE_SHEET_URL_BY_SLUG[slug] ?? null
}
